import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { RegistrationService } from '../../registration.service';
import { GlobalSearchStakeholderComponent } from '../../../../shared/components/global-search-stakeholder/global-search-stakeholder.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RegisterForMeetingRequestDTO } from '../../types/RegisterForMeetingRequestDTO';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { PublicOnlineMeetingDTO } from '../../types/PublicOnlineMeetingDTO';
import { GTranslateWidgetService } from 'src/app/shared/services/gtranslate-widget.service';

@Component({
  selector: 'app-online-registration',
  imports: [
    CommonModule,
    GlobalSearchStakeholderComponent,
    MatCheckbox,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInput,
    MatLabel,
  ],
  templateUrl: './online-registration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './online-registration.component.scss',
})
export class OnlineRegistrationComponent implements OnInit, OnDestroy {
  private subscription: Subscription | null = null;
  meetingInfo: PublicOnlineMeetingDTO | null = null;
  isStakeholderSearchValid: boolean = false;
  registrationForm: FormGroup;
  onStakeholderSearch: number = 0;

  constructor(
    private registrationService: RegistrationService,
    private urlParamsService: UrlParamsService,
    private fb: FormBuilder,
    private gTranslateService: GTranslateWidgetService
  ) {
    this.registrationForm = this.fb.group({
      StakeGuid: ['', Validators.required],
      MeetingGuid: ['', Validators.required],
      IsOnline: [true, Validators.required],
      TotalAttendees: [1, Validators.required],
      IsAnonymous: [false],
      IsMultipleAttending: [false],
      ExtraAttendees: [0],
      IsSubscribed: [false],
    });
  }

  ngOnInit(): void {
    this.subscription = this.registrationService.onlineMeetingInfo$.subscribe({
      next: (meetingInfo) => {
        this.meetingInfo = meetingInfo;
        this.registrationForm.get('MeetingGuid')?.setValue(meetingInfo?.MeetingGuid);
      },
      error: (error) => {
        console.error('Error fetching meeting info:', error);
      },
    });

    this.registrationForm.get('IsMultipleAttending')?.valueChanges.subscribe((isMultiple) => {
      if (!isMultiple) {
        this.registrationForm.get('ExtraAttendees')?.setValue(0);
      } else {
        setTimeout(() => this.gTranslateService.retranslate());
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  handleStakeholderSearchResult(event: { status: string; data: any; formValues: any }): void {
    switch (event.status) {
      case 'success':
        if (event.data && event.data.StakeGuid) {
          this.registerForMeeting({
            StakeGuid: event.data.StakeGuid,
            MeetingGuid: this.registrationForm.get('MeetingGuid')?.value,
            IsOnline: this.registrationForm.get('IsOnline')?.value,
            TotalAttendees: this.calculateTotalAttendees(),
            IsAnonymous: this.registrationForm.get('IsAnonymous')?.value,
          });
        } else {
          this.urlParamsService.navigateWithParams('/registration/new-stakeholder', {
            LastName: event.formValues.LastName,
            Mail1ZipCode: event.formValues.Mail1ZipCode,
            StakeEmail: event.formValues.StakeEmail,
            SubscribeSourceId: 3,
            isEdit: false,
            backUrl: '/registration/online',
            registrationMode: 'online',
          });
        }

        break;
      case 'error':
        console.error('Error fetching stakeholder:', event.data);
        this.urlParamsService.navigateToErrorPage('Error searching for stakeholder.');
        break;
      default:
        break;
    }
  }

  handleFormValidity(event: boolean): void {
    this.isStakeholderSearchValid = event;
  }

  isSubmitDisabled(): boolean {
    return !this.registrationForm.get('IsAnonymous')?.value && !this.isStakeholderSearchValid;
  }

  async onRegisterForMeeting(): Promise<void> {
    if (this.registrationForm.get('IsAnonymous')?.value) {
      try {
        const anonStakeGuid = await this.getAnonymousStakeholderGuid();
        if (this.meetingInfo) {
          this.registerForMeeting({
            StakeGuid: anonStakeGuid,
            MeetingGuid: this.meetingInfo.MeetingGuid,
            IsOnline: this.registrationForm.get('IsOnline')?.value,
            TotalAttendees: this.calculateTotalAttendees(),
            IsAnonymous: true,
          });
        }
      } catch (error) {
        console.error('Error during registration:', error);
        this.urlParamsService.navigateToErrorPage(
          'An error occurred while registering for the meeting.'
        );
      }
    } else {
      // Emits a value to the stake search component to run the search API call
      this.onStakeholderSearch++;
    }
  }

  private registerForMeeting(registrant: RegisterForMeetingRequestDTO): void {
    this.registrationService.registerStakeholderForMeeting(registrant).subscribe({
      next: (response: { url?: string }) => {
        this.urlParamsService.navigateWithParams('/registration/online/confirmation', {
          onlineMeetingUrl: response?.url,
        });
      },
      error: (error) => {
        console.error('Error registering stakeholder for meeting:', error);
        this.onStakeholderSearch = 0;
        this.urlParamsService.navigateToErrorPage(
          'An error occurred while registering for the meeting.'
        );
      },
    });
  }

  private async getAnonymousStakeholderGuid(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.registrationService.getAnonymousStakeholder().subscribe({
        next: (response) => resolve(response.StakeGuid),
        error: (error) => {
          console.error('Error fetching anonymous stakeholder:', error);
          reject('');
          this.urlParamsService.navigateToErrorPage('An error occurred.');
        },
      });
    });
  }

  private calculateTotalAttendees(): number {
    return (
      (this.registrationForm.get('TotalAttendees')?.value || 1) +
      (this.registrationForm.get('ExtraAttendees')?.value || 0)
    );
  }
}
